import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { Container, Grid, Typography, Box } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useSnackbar } from "notistack";

import { STATUS } from "../../../redux/constants";
import { sampleYear, months } from "../../../constants";
import { waterSupplyValidation } from "./schema";
import {
  resetAddCombustionStatus,
  deleteEmissions,
  updateWaterSupply,
  getEmissionInputFormat,
} from "../../../redux/actions";

import CeroAutoComplete from "../../../components/CeroAutoComplete";
import CeroButton from "../../../components/CeroButton";
import CeroSelect from "../../../components/CeroSelect";
import CeroInput from "../../../components/CeroInput";
import CeroInfoPair from "../../../components/CeroInfoPair";
import CeroConfirmDrawer from "../../../components/CeroConfirmDrawer";
import useStyles from "./styles";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useNavigate } from "react-router-dom";

const EditWaterSupplyForm = (props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { emissionId, emissionData, facilitiesData, onCancel, emissionInputs } =
    props;
  const storeEmissionInputs =
    useSelector((s) => s.emission.emissionInputs.data) || {};

  const inputs = emissionInputs ?? storeEmissionInputs;

  useEffect(() => {
    if (!inputs || Object.keys(inputs).length === 0) {
      dispatch(getEmissionInputFormat("water_supply"));
    }
  }, [dispatch, inputs]);

  const [displayWarning, setDisplayWarning] = useState(false);

  const isCalculateDone = useSelector(
    (state) => state.emission.updateWaterSupply.isCalculateDone
  );
  const updateEmissionData = useSelector(
    (state) => state.emission.updateWaterSupply
  );
  const deleteEmissionData = useSelector(
    (state) => state.emission.deleteEmissions
  );

  const facilitiesList = facilitiesData.map((item) => ({
    key: item.id,
    value: item.name,
  }));
  const stressTypeList = (inputs?.water_source_stress_types ?? []).map(
    (it) => ({
      key: String(it.id),
      value: it.name,
    })
  );

  const sourceTypeList = (inputs?.water_source_types ?? []).map((it) => ({
    key: String(it.id),
    value: it.name,
  }));

  const sourceList = (inputs?.water_sources ?? []).map((it) => ({
    key: String(it.id),
    value: it.name,
  }));

  const fuelUnits = (inputs?.units ?? []).map((u) => ({
    key: u.name,
    value: u.name,
  }));
  const yearList = sampleYear().map((item) => ({
    id: item.key,
    label: item.value,
  }));

  const formik = useFormik({
    initialValues: {
      facility: emissionData.facility_id ?? "",
      year: Number(emissionData.year) || "",
      month: Number(emissionData.month) || "",
      source: emissionData.water_source_id ?? "",
      stressType: emissionData.water_source_stress_type_id ?? "",
      sourceType: emissionData.water_source_type_id ?? "",
      fuelUnit: emissionData.usage_unit ?? "",
      amountOfFuel: emissionData.usage ?? "",
    },

    validationSchema: waterSupplyValidation,
    onSubmit: () => {},
  });

  useEffect(() => {
    formik.setValues((v) => ({
      ...v,
      facility: v.facility !== "" ? String(v.facility) : "",
      source: v.source !== "" ? String(v.source) : "",
      stressType: v.stressType !== "" ? String(v.stressType) : "",
      sourceType: v.sourceType !== "" ? String(v.sourceType) : "",
      fuelUnit: v.fuelUnit ?? "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const yearValue =
    yearList.find((o) => o.id === Number(formik.values.year)) || null;

  useEffect(() => {
    if (updateEmissionData.status === STATUS.SUCCESS) {
      enqueueSnackbar("Water Supply updated successfully", {
        variant: "success",
      });
      dispatch(resetAddCombustionStatus());
      onCancel();
    } else if (updateEmissionData.status === STATUS.ERROR) {
      enqueueSnackbar(
        updateEmissionData.message.message ||
          "We couldn't process your request. Please try again later.",
        { variant: "error" }
      );
      dispatch(resetAddCombustionStatus());
    }
  }, [updateEmissionData, enqueueSnackbar, onCancel, dispatch]);

  useEffect(() => {
    if (deleteEmissionData.status === STATUS.SUCCESS) {
      enqueueSnackbar("Water supply deleted successfully", {
        variant: "success",
      });
      dispatch(resetAddCombustionStatus());
      onCancel();
    } else if (deleteEmissionData.status === STATUS.ERROR) {
      enqueueSnackbar(
        deleteEmissionData.message.message ||
          "We couldn't process your request. Please try again later.",
        { variant: "error" }
      );
      dispatch(resetAddCombustionStatus());
    }
  }, [deleteEmissionData, enqueueSnackbar, onCancel, dispatch]);

  //
  const buildPayload = (save) => {
    const amount = Number(formik.values.amountOfFuel);
    return {
      id: emissionId,
      facility_id: formik.values.facility,
      year: Number(formik.values.year) || 0,
      month: formik.values.month ? Number(formik.values.month) : 0,
      water_source_id: formik.values.source,
      water_source_stress_type_id: formik.values.stressType,
      water_source_type_id: formik.values.sourceType,
      usage_unit: formik.values.fuelUnit,
      usage: Number.isFinite(amount) ? amount : 0,
      save,
    };
  };

  const onCalculate = () => {
    dispatch(updateWaterSupply(buildPayload(false)));
  };

  const onUpdateWaterSupply = () => {
    dispatch(updateWaterSupply(buildPayload(true)));
  };

  const onConfirmDelete = () => {
    const requestData = {
      id: emissionId,
    };
    dispatch(deleteEmissions(requestData));
  };

  return (
    <>
      <div
        className={classes.backContainer}
        onClick={() => {
          navigate(-1);
        }}
      >
        <ArrowBackIosIcon />
        Back
      </div>
      <Container className={classes.container}>
        <Box className={classes.innerContainer}>
          <Typography className={classes.title} variant="h6" component="div">
            Edit Water Supply
          </Typography>
          <Box className={classes.topContainer}>
            <Grid
              container
              direction="row"
              wrap="nowrap"
              justifyContent="space-between"
              spacing={8}
            >
              <Grid item container direction="column" md={6} xs={12}>
                <CeroSelect
                  required
                  id="facility"
                  name="facility"
                  label="Facility"
                  fullWidth
                  options={facilitiesList}
                  selectedValue={formik.values.facility}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.facility && formik.errors.facility}
                />
                <CeroSelect
                  required
                  id="month"
                  name="month"
                  label="Month"
                  fullWidth
                  options={months}
                  selectedValue={formik.values.month}
                  //   onChange={formik.handleChange}
                  onChange={(e) =>
                    formik.setFieldValue("month", Number(e.target.value))
                  }
                  onBlur={formik.handleBlur}
                  error={formik.touched.month && formik.errors.month}
                />
                <CeroSelect
                  required
                  id="sourceType"
                  name="sourceType"
                  label="Water Source type"
                  fullWidth
                  options={sourceTypeList}
                  selectedValue={formik.values.sourceType}
                  //   onChange={formik.handleChange}
                  onChange={(e) =>
                    formik.setFieldValue("sourceType", e.target.value)
                  }
                  onBlur={formik.handleBlur}
                  error={formik.touched.sourceType && formik.errors.sourceType}
                  classes={{
                    input: classes.input,
                    container: classes.selectContainer,
                  }}
                />
                <CeroInput
                  required
                  id="amountOfFuel"
                  name="amountOfFuel"
                  label="Amount of Water"
                  value={formik.values.amountOfFuel}
                  fullWidth
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.amountOfFuel && formik.errors.amountOfFuel
                  }
                  type="number"
                />
              </Grid>
              <Grid item container direction={"column"} md={6} xs={12}>
                <CeroSelect
                  required
                  id="source"
                  name="source"
                  label="Water Sources"
                  fullWidth
                  options={sourceList}
                  selectedValue={formik.values.source}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.source && formik.errors.source}
                />
                <CeroAutoComplete
                  id="year"
                  label="Year"
                  value={yearValue}
                  onChange={(e, value) =>
                    formik.setFieldValue("year", value?.id ?? "")
                  }
                  onBlur={formik.handleBlur}
                  error={formik.touched.year && formik.errors.year}
                  options={yearList}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                />
                <CeroSelect
                  required
                  id="stressType"
                  name="stressType"
                  label="water Stress type"
                  fullWidth
                  options={stressTypeList}
                  selectedValue={formik.values.stressType}
                  //   onChange={formik.handleChange}
                  onChange={(e) =>
                    formik.setFieldValue("stressType", e.target.value)
                  }
                  onBlur={formik.handleBlur}
                  error={formik.touched.stressType && formik.errors.stressType}
                  classes={{
                    input: classes.input,
                    container: classes.selectContainer,
                    paperRoot: classes.paperRoot,
                  }}
                />
                <CeroSelect
                  required
                  id="fuelUnit"
                  name="fuelUnit"
                  label="Unit"
                  fullWidth
                  options={fuelUnits}
                  selectedValue={formik.values.fuelUnit}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.fuelUnit && formik.errors.fuelUnit}
                />
              </Grid>
            </Grid>
            <CeroButton
              buttonText="Calculate"
              className={clsx(classes.button, classes.buttonPrimary)}
              onClick={onCalculate}
              disabled={!formik.dirty || !formik.isValid}
            />
            <CeroButton
              buttonText="Clear"
              variant="outlined"
              className={clsx(classes.button, classes.buttonSecondary)}
              onClick={formik.resetForm}
            />
            {props.isDeleteEnable && (
              <CeroButton
                buttonText={<DeleteOutlineIcon />}
                className={clsx(classes.button, classes.deleteButton)}
                onClick={() => setDisplayWarning(true)}
              />
            )}
          </Box>
          {isCalculateDone && (
            <Box className={classes.bottomContainer}>
              <Typography
                variant="h6"
                component="h6"
                className={classes.previewTitle}
              >
                Emission Preview
              </Typography>
              <Grid
                container
                direction="row"
                wrap="nowrap"
                justifyContent="space-between"
                spacing={8}
              >
                <Grid item container direction="column" xs={12} md={6}>
                  <CeroInfoPair
                    title="Usage"
                    value={
                      <span style={{ textTransform: "none" }}>
                        {updateEmissionData?.data?.usage ?? ""}{" "}
                        {updateEmissionData?.data?.usage_unit ?? ""}
                      </span>
                    }
                  />
                  <CeroInfoPair
                    title={
                      <>
                        CO<sub>2</sub>e
                      </>
                    }
                    value={
                      <span style={{ textTransform: "none" }}>
                        {updateEmissionData?.data?.co2e ?? ""}{" "}
                        {updateEmissionData?.data?.emission_unit ?? ""}
                      </span>
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
        <Box className={classes.buttonContainer}>
          <CeroButton
            buttonText="Update"
            disabled={!isCalculateDone}
            className={clsx(classes.button, classes.buttonPrimary)}
            onClick={onUpdateWaterSupply}
          />
        </Box>
        {displayWarning && (
          <CeroConfirmDrawer
            isOpen={displayWarning}
            onClose={() => setDisplayWarning(false)}
            onConfirm={onConfirmDelete}
          />
        )}
      </Container>
    </>
  );
};

export default EditWaterSupplyForm;
